import type { ComponentProps, ForwardedRef } from 'react';
import React, { useRef } from 'react';
import styled from '@emotion/styled';
import type { SignupFlow } from 'corgi-types';
import type { AuthenticationProvider } from 'graphql-ts-types';
import { ThirdPartyProvider } from 'graphql-ts-types';
import type { MaxHeightProps, MinHeightProps } from 'styled-system';
import { maxHeight, minHeight } from 'styled-system';

import AutoScrollDrawerAlert from 'common/components/AutoScrollAlert/AutoScrollDrawerAlert';
import { ButtonLink } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import Modal from 'common/components/Modal';
import SeparatorWithText from 'common/components/SeparatorWithText';
import { BodyRegularBR1, BodyRegularBR2 } from 'common/components/Typography';
import { useTranslatedErrorMessageState } from 'common/hooks/form/error';
import { useLoginFormUrls } from 'common/hooks/form/login';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { OverflowYProps } from 'common/theme/system-utilities';
import { overflowY } from 'common/theme/system-utilities';
import { Event } from 'common/types/auth';
import { Element } from 'common/types/tracking';
import { getUserFlowQueryParam } from 'common/utils/signup';
import PasswordForm from '../Form/PasswordForm';
import AppleButton from './AppleSignInButton';
import GoogleButton from './GoogleButton';
import type { PerformLogin } from './hooks/third-party-login';
import {
  isUserOnlyRegularUser,
  isUserOnlyThirdPartyUser,
  isUserRegularAndThirdPartyUser,
} from './utils';

interface PasswordModalProps {
  isOpen: boolean;
  email: string;
  userFlow: SignupFlow;
  existentAssociations: AuthenticationProvider[];
  onModalClose: () => void;
  onSuccess: () => void;
  provider: ThirdPartyProvider;
  performLogin: PerformLogin;
  appleStateToken?: string;
  appleClientId?: string;
}

const Container = styled(Flex)<
  MaxHeightProps & MinHeightProps & OverflowYProps & { ref: ForwardedRef<HTMLElement> }
>`
  ${maxHeight};
  ${minHeight};
  ${overflowY};
`;

const containerProps: Partial<ComponentProps<typeof Container>> = {
  as: 'article',
  flexDirection: 'column',
  p: 'sp_16',
  px: 'sp_16',
  pb: ['sp_16', 'sp_32'],
  minHeight: '250px',
  maxHeight: '450px',
  overflowY: 'auto',
};

const getFlags = ({
  provider,
  appleClientId,
  appleStateToken,
  existentAssociations,
}: Pick<
  PasswordModalProps,
  'existentAssociations' | 'provider' | 'appleClientId' | 'appleStateToken'
>) => {
  const shouldShowPasswordForm =
    isUserOnlyRegularUser(existentAssociations) ||
    isUserRegularAndThirdPartyUser(existentAssociations);

  const shouldShowSetPasswordLink = isUserOnlyThirdPartyUser(existentAssociations);

  const shouldShowThirdPartyButtons =
    isUserRegularAndThirdPartyUser(existentAssociations) ||
    isUserOnlyThirdPartyUser(existentAssociations);

  const shouldShowGoogleButton =
    shouldShowThirdPartyButtons && provider === ThirdPartyProvider.Apple;

  const shouldShowAppleButton =
    shouldShowThirdPartyButtons &&
    provider === ThirdPartyProvider.Google &&
    appleClientId &&
    appleStateToken;

  return {
    shouldShowAppleButton,
    shouldShowGoogleButton,
    shouldShowPasswordForm,
    shouldShowSetPasswordLink,
  };
};

// eslint-disable-next-line max-lines-per-function
const PasswordModal = ({
  isOpen,
  email,
  userFlow,
  onModalClose,
  onSuccess,
  provider,
  performLogin,
  appleClientId,
  appleStateToken,
  existentAssociations,
}: PasswordModalProps) => {
  const { t, r } = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);
  const { forgotPasswordUrl } = useLoginFormUrls(userFlow);
  const [error, setError] = useTranslatedErrorMessageState();

  const handleModalClose = () => {
    setError(null);
    onModalClose();
  };

  const setThirdPartyLoginError = () => {
    setError(
      r.t('h24_third_party_login_error', {
        forgotPasswordUrl,
      })
    );
  };

  const {
    shouldShowAppleButton,
    shouldShowGoogleButton,
    shouldShowPasswordForm,
    shouldShowSetPasswordLink,
  } = getFlags({
    provider,
    appleClientId,
    appleStateToken,
    existentAssociations,
  });

  const descriptionTextKey = shouldShowSetPasswordLink
    ? 'h24_third_party_login_social_account_exists'
    : 'h24_third_party_login_account_exists';

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} headerTitle={t('h24_register')}>
      <Container
        {...containerProps}
        {...(shouldShowGoogleButton
          ? {
              px: ['sp_16', 'sp_64'],
            }
          : {})}
        ref={containerRef}
      >
        {error && (
          <AutoScrollDrawerAlert
            containerRef={containerRef}
            messageId={error.timestamp}
            message={error.message}
            mb="sp_16"
            mt="sp_0"
            mx="sp_0"
          />
        )}

        <BodyRegularBR1
          dangerouslySetInnerHTML={{
            __html: r.t(descriptionTextKey, { email }),
          }}
          mb="sp_16"
        />

        {shouldShowPasswordForm && (
          <PasswordForm
            setCommonError={setError}
            onSuccess={onSuccess}
            userFlow={userFlow}
            email={email}
          />
        )}

        {shouldShowSetPasswordLink && (
          <ButtonLink
            kind="secondary-v2"
            href={`./forgotpassword${getUserFlowQueryParam(userFlow)}`}
            tracking={{
              element: Element.Link,
              eventName: Event.LoginFormClick,
              text: t('h24_set_password'),
              placement: 'modal',
            }}
          >
            {t('h24_set_password')}
          </ButtonLink>
        )}

        {(shouldShowGoogleButton || shouldShowAppleButton) && (
          <SeparatorWithText my="sp_16">
            <BodyRegularBR2>{t('h24_or')}</BodyRegularBR2>
          </SeparatorWithText>
        )}

        {shouldShowGoogleButton && (
          <GoogleButton
            setCommonError={setThirdPartyLoginError}
            performLogin={performLogin}
            placement="modal"
            mt={0}
          />
        )}

        {shouldShowAppleButton && appleClientId && appleStateToken && (
          <AppleButton
            setCommonError={setThirdPartyLoginError}
            stateToken={appleStateToken}
            performLogin={performLogin}
            clientId={appleClientId}
            placement="modal"
          />
        )}
      </Container>
    </Modal>
  );
};

export default PasswordModal;
